import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SpecValue } from "@/types/specList";

type SpecDetailsSidePanelProps = {
  specValue: SpecValue | null;
  onClose: () => void;
};

const SpecField = ({ label, value }: { label: string; value: string | number | undefined }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    <span className="text-sm">{value ?? "-"}</span>
  </div>
);

const CompactSpecValueField = ({
  label,
  value,
  previousValue,
}: {
  label: string;
  value: number | undefined;
  previousValue?: number;
}) => {
  const hasChanged = previousValue !== undefined && value !== previousValue;

  return (
    <div className="flex flex-col gap-1 min-w-0">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="flex items-baseline gap-2">
        <span className={`text-sm font-medium ${hasChanged ? "text-blue-600" : ""}`}>
          {value ?? "-"}
        </span>
        {hasChanged && previousValue !== undefined && (
          <span className="text-xs text-muted-foreground/60">(mst: {previousValue})</span>
        )}
      </div>
    </div>
  );
};

export const SpecDetailsSidePanel = ({ specValue, onClose }: SpecDetailsSidePanelProps) => {
  if (!specValue) return null;

  return (
    <div
      className="fixed top-[5%] bottom-[5%] right-4 z-50 bg-background shadow-2xl border rounded-lg overflow-hidden flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="bg-background px-6 py-4 flex-shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold truncate">{specValue.spec.name}</h2>
            {!specValue.isMasterValue && (
              <p className="text-xs text-orange-600 mt-1">Modified from master version</p>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="flex-shrink-0">
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Basic Information */}
        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-sm">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-8">
            <SpecField label="Report Name" value={specValue.spec.reportName} />
            <SpecField label="Legacy Names" value={specValue.spec.legacyNames} />
            <SpecField label="DUT Names" value={specValue.spec.dutNames} />
            <SpecField label="Device Class" value={specValue.spec.deviceClass} />
            <SpecField label="Condition" value={specValue.spec.condition} />
            <SpecField label="Recipe" value={specValue.spec.recipe?.name} />
          </CardContent>
        </Card>

        {/* Spec Values */}
        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-sm">Spec Limits</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-4">
            <CompactSpecValueField
              label="LSL"
              value={specValue.lsl}
              previousValue={specValue.previousValues?.lsl}
            />
            <CompactSpecValueField
              label="Target"
              value={specValue.tgt}
              previousValue={specValue.previousValues?.tgt}
            />
            <CompactSpecValueField
              label="USL"
              value={specValue.usl}
              previousValue={specValue.previousValues?.usl}
            />
          </CardContent>
        </Card>

        {/* JMP Values */}
        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-sm">JMP Settings</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-4">
            <CompactSpecValueField
              label="Y Min"
              value={specValue.jmpYmin}
              previousValue={specValue.previousValues?.jmpYmin}
            />
            <CompactSpecValueField
              label="Y Max"
              value={specValue.jmpYmax}
              previousValue={specValue.previousValues?.jmpYmax}
            />
            <CompactSpecValueField
              label="Y Inc"
              value={specValue.jmpYinc}
              previousValue={specValue.previousValues?.jmpYinc}
            />
          </CardContent>
        </Card>

        {/* Library Statistics */}
        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-sm">Library Statistics</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-4">
            <CompactSpecValueField
              label="Median"
              value={specValue.libMedian}
              previousValue={specValue.previousValues?.libMedian}
            />
            <CompactSpecValueField
              label="Mean"
              value={specValue.libMean}
              previousValue={specValue.previousValues?.libMean}
            />
            <CompactSpecValueField
              label="Std Dev"
              value={specValue.libStd}
              previousValue={specValue.previousValues?.libStd}
            />
            <CompactSpecValueField
              label="Q10"
              value={specValue.libQ10}
              previousValue={specValue.previousValues?.libQ10}
            />
            <CompactSpecValueField
              label="Q90"
              value={specValue.libQ90}
              previousValue={specValue.previousValues?.libQ90}
            />
            <CompactSpecValueField
              label="N"
              value={specValue.libN}
              previousValue={specValue.previousValues?.libN}
            />
          </CardContent>
        </Card>

        {/* Helper text */}
        {specValue.previousValues && (
          <p className="text-xs text-muted-foreground text-center pt-2">
            Values in <span className="text-blue-600 font-medium">blue</span> have changed from the
            master version
          </p>
        )}
      </div>
    </div>
  );
};
